import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { getApiUrl } from '../utils/apiConfig';

const KurseContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const CourseCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.card};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.hover};
  }
`;

const CourseImage = styled.div`
  height: 200px;
  background: ${props => props.gradient || props.theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
`;

const CourseContent = styled.div`
  padding: 2rem;
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  .features {
    list-style: none;
    padding: 0;
    margin-bottom: 2rem;
    
    li {
      padding: 0.25rem 0;
      color: ${props => props.theme.colors.textLight};
      
      &::before {
        content: '✓';
        color: ${props => props.theme.colors.primary};
        font-weight: bold;
        margin-right: 0.5rem;
      }
    }
  }
`;

const CourseButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(46, 204, 113, 0.4);
  }
`;

const BookNowButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(46, 204, 113, 0.4);
  }
  
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
  color: ${props => props.text || '#065f46'};
  background: ${props => props.bg || '#d1fae5'};
`;

const Kurse = () => {
  const [publicCourses, setPublicCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function loadCourses() {
      try {
        setLoading(true);
        const res = await fetch(getApiUrl('/public/courses'));
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Fehler beim Laden der Kurse');
        }
        if (isMounted) setPublicCourses(data.data || []);
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadCourses();
    return () => { isMounted = false; };
  }, []);
  const courses = [
    {
      id: 'ponyclub',
      title: 'Ponyclub',
      emoji: '🎠',
      description: 'Spielerischer Umgang mit Ponys für Kinder ab 4 Jahren. Hier lernen die Kleinen den respektvollen Umgang mit den Tieren.',
      features: [
        'Für Kinder ab 4 Jahren',
        'Spielerisches Lernen',
        'Kleine Gruppen (max. 8 Kinder)',
        'Grundlagen der Pferdepflege',
        'Erste Reiterfahrungen'
      ],
      gradient: props => props.theme.colors.gradients.ponyclub,
      link: '/kurse/ponyclub'
    },
    {
      id: 'muki',
      title: 'MuKi-Club',
      emoji: '👶',
      description: 'Mutter-Kind-Kurse für die ganz Kleinen ab 2 Jahren. Gemeinsame Erlebnisse mit Mama oder Papa und den Ponys.',
      features: [
        'Für Kinder ab 2 Jahren + Begleitperson',
        'Gemeinsame Erlebnisse',
        'Frühe Tierverbindung',
        'Motorik-Förderung',
        'Soziale Kompetenz'
      ],
      gradient: props => props.theme.colors.gradients.muki,
      link: '/kurse/muki-club'
    },
    {
      id: 'therapeutic',
      title: 'Heilpädagogisches Reiten',
      emoji: '🌟',
      description: 'Professionelle tiergestützte Therapie für Menschen mit besonderen Bedürfnissen. Individuelle Förderung und Entwicklung.',
      features: [
        'Individuelle Förderung',
        'Therapeutischer Ansatz',
        'Alle Altersgruppen',
        'Spezielle Bedürfnisse',
        'Entwicklungsförderung'
      ],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      link: '/kurse/heilpaedagogisches-reiten'
    }
  ];

  return (
    <KurseContainer>
      <Helmet>
        <title>Unsere Kurse - Der Ponystall | Ponyclub, MuKi-Club & Heilpädagogisches Reiten</title>
        <meta name="description" content="Entdecken Sie unsere vielfältigen Kurse: Ponyclub für Kinder, MuKi-Club für die Kleinsten und heilpädagogisches Reiten für besondere Bedürfnisse." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Unsere Kurse
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Vielfältige Angebote für jedes Alter</h2>
            <p className="section-subtitle">
              Von spielerischen Ponyclub-Stunden bis hin zur professionellen tiergestützten Therapie - 
              bei uns findet jeder das passende Angebot.
            </p>
          </div>

          <CoursesGrid>
            {courses.map((course, index) => (
              <CourseCard
                key={course.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <CourseImage gradient={course.gradient}>
                  {course.emoji}
                </CourseImage>
                <CourseContent>
                  <h3>{course.title}</h3>
                  <p>{course.description}</p>
                  <ul className="features">
                    {course.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                  <CourseButton to={course.link}>
                    Mehr erfahren
                  </CourseButton>
                </CourseContent>
              </CourseCard>
            ))}
          </CoursesGrid>
        </Container>
      </ContentSection>

      <ContentSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Aktuelle Kursangebote</h2>
            <p className="section-subtitle">
              Direkt buchbare Kurse
            </p>
          </div>

          {error && (
            <div style={{ color: '#b91c1c', marginTop: '1rem', textAlign: 'center' }}>{error}</div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Kurse werden geladen...</div>
          ) : (
            <CoursesGrid>
              {publicCourses.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280' }}>
                  Aktuell sind keine Kurse verfügbar.
                </div>
              ) : (
                publicCourses.map((course, index) => (
                  <CourseCard
                    key={course.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <CourseImage>
                      {course.title || '🐴'}
                    </CourseImage>
                    <CourseContent>
                      <div style={{ marginBottom: '1.5rem' }}>
                        {course.is_full && (
                          <Badge bg="#fee2e2" text="#991b1b">Ausgebucht</Badge>
                        )}
                        {course.category && (
                          <Badge>{course.category === 'ponyclub' ? 'Ponyclub' : course.category === 'muki' ? 'MuKi-Club' : course.category === 'heilpaedagogik' ? 'Heilpädagogik' : course.category}</Badge>
                        )}
                      </div>
                      <p>{course.description || 'Keine Beschreibung verfügbar.'}</p>
                      <ul className="features">
                        <li>Preis: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: course.currency || 'EUR' }).format(course.price || 0)}</li>
                        <li>Verfügbare Plätze: {course.available_spots ?? '-'}</li>
                        {course.schedule_text && <li>Zeiten: {course.schedule_text}</li>}
                      </ul>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <BookNowButton
                          href={course.is_full ? undefined : `/kurse/buchen/${course.slug}/jetzt`}
                          aria-label={`Jetzt ${course.title} buchen`}
                          className={course.is_full ? 'disabled' : ''}
                          onClick={(e) => { if (course.is_full) e.preventDefault(); }}
                        >
                          {course.is_full ? 'Ausgebucht' : 'Jetzt buchen'}
                        </BookNowButton>
                        <BookNowButton
                          href={`/kurse/buchen/${course.slug}`}
                          aria-label={`Mehr Details zu ${course.title}`}
                          className={course.is_full ? 'disabled' : ''}
                          onClick={(e) => { if (course.is_full) e.preventDefault(); }}
                        >
                          Mehr Details
                        </BookNowButton>
                      </div>
                    </CourseContent>
                  </CourseCard>
                ))
              )}
            </CoursesGrid>
          )}
        </Container>
      </ContentSection>
    </KurseContainer>
  );
};

export default Kurse;